import type {
  CSSProperties,
  MouseEvent,
  MutableRefObject,
  ReactNode,
  RefObject,
  SyntheticEvent,
} from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { Col } from 'antd';

import type { Banner } from '@eshop/types/banners.types.ts';
import type { TemplateGridSpan } from '@eshop/templates';
import { parseAspectValue } from '@eshop/templates/visual/shared';

const DEFAULT_BREAKPOINTS: TemplateGridSpan = { xs: { span: 24 } };

export const CATALOG_BANNER_ASPECT_TOKEN = '--catalogue-banner-aspect';

export interface CatalogBannerRowMedia {
  src: string;
  srcSet?: string;
  sizes?: string;
  fallback?: string;
  loading?: 'eager' | 'lazy';
  decoding?: 'async' | 'auto' | 'sync';
  fetchPriority?: 'high' | 'low' | 'auto';
  aspectRatio?: string;
  onError?: (event: SyntheticEvent<HTMLImageElement>) => void;
}

type DivRef =
  | RefObject<HTMLDivElement>
  | RefObject<HTMLDivElement | null>
  | MutableRefObject<HTMLDivElement | null>;

type HeadingRef =
  | RefObject<HTMLHeadingElement>
  | RefObject<HTMLHeadingElement | null>
  | MutableRefObject<HTMLHeadingElement | null>;

type ParagraphRef =
  | RefObject<HTMLParagraphElement>
  | RefObject<HTMLParagraphElement | null>
  | MutableRefObject<HTMLParagraphElement | null>;

type SpanRef =
  | RefObject<HTMLSpanElement>
  | RefObject<HTMLSpanElement | null>
  | MutableRefObject<HTMLSpanElement | null>;

export interface CatalogBannerRowProps {
  banner: Banner;
  breakpoints?: TemplateGridSpan;
  containerRef?: DivRef;
  mediaWrapperRef?: DivRef;
  textContainerRef?: DivRef;
  headlineRef?: HeadingRef;
  bodyRef?: ParagraphRef;
  ctaRef?: SpanRef;
  media: CatalogBannerRowMedia;
  role?: string;
  ariaLabel?: string;
  className?: string;
  children?: ReactNode;
  renderAsChild?: boolean;
  onBannerMouseEnter?: (event: MouseEvent<HTMLDivElement>) => void;
  onBannerMouseLeave?: (event: MouseEvent<HTMLDivElement>) => void;
  onMediaMouseEnter?: (event: MouseEvent<HTMLImageElement>) => void;
  onMediaMouseLeave?: (event: MouseEvent<HTMLImageElement>) => void;
  onLinkClick?: () => void;
}

const getAriaLabel = (banner: Banner, provided?: string) => {
  if (provided) {
    return provided;
  }

  if (banner.headline?.trim()) {
    return banner.headline.trim();
  }

  if (banner.body?.trim()) {
    return banner.body.trim();
  }

  return 'Banner del catálogo';
};

type BreakpointStyle = CSSProperties & Record<string, string | number>;

export const buildBreakpointStyles = (breakpoints: TemplateGridSpan): BreakpointStyle => {
  const style: BreakpointStyle = { gridColumn: '1 / -1' };

  (Object.entries(breakpoints) as Array<[
    keyof TemplateGridSpan,
    number | TemplateGridSpan[keyof TemplateGridSpan],
  ]>).forEach(([breakpoint, value]) => {
    const prefix = `--ant-col-${breakpoint}`;

    if (typeof value === 'number') {
      style[`${prefix}-span`] = String(value);
      return;
    }

    if (!value) {
      return;
    }

    if (typeof value.span === 'number') {
      style[`${prefix}-span`] = String(value.span);
    }

    if (value.flex !== undefined) {
      style[`${prefix}-flex`] = String(value.flex);
    }

    if (value.offset !== undefined) {
      style[`${prefix}-offset`] = String(value.offset);
    }

    if (value.order !== undefined) {
      style[`${prefix}-order`] = String(value.order);
    }

    if (value.push !== undefined) {
      style[`${prefix}-push`] = String(value.push);
    }

    if (value.pull !== undefined) {
      style[`${prefix}-pull`] = String(value.pull);
    }
  });

  return style;
};

const CatalogBannerRow = ({
  banner,
  breakpoints,
  containerRef,
  mediaWrapperRef,
  textContainerRef,
  headlineRef,
  bodyRef,
  ctaRef,
  media,
  role = 'region',
  ariaLabel,
  className,
  children,
  renderAsChild,
  onBannerMouseEnter,
  onBannerMouseLeave,
  onMediaMouseEnter,
  onMediaMouseLeave,
  onLinkClick,
}: CatalogBannerRowProps) => {
  const aria = getAriaLabel(banner, ariaLabel);
  const {
    src,
    srcSet,
    sizes,
    fallback,
    loading = 'lazy',
    decoding,
    fetchPriority,
    aspectRatio,
    onError,
  } = media;
  const resolvedDecoding = decoding ?? (loading === 'eager' ? 'sync' : 'async');
  const colBreakpoints =
    !renderAsChild && breakpoints && Object.keys(breakpoints).length > 0
      ? breakpoints
      : DEFAULT_BREAKPOINTS;

  const bannerClassName = ['catalog-banner', className]
    .filter(Boolean)
    .join(' ');

  const colStyle = useMemo(() => buildBreakpointStyles(colBreakpoints), [colBreakpoints]);
  const mediaStyle = useMemo(() => {
    const parsed = parseAspectValue(aspectRatio);
    if (!parsed) {
      return undefined;
    }
    return {
      [CATALOG_BANNER_ASPECT_TOKEN as string]: parsed,
      aspectRatio: parsed,
    } as CSSProperties;
  }, [aspectRatio]);

  const hasAppliedFallback = useRef(false);
  const imageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const element = imageRef.current;
    if (!element) {
      return;
    }

    try {
      Object.defineProperty(element, 'loading', {
        configurable: true,
        writable: true,
        value: loading,
      });
    } catch (error) {
      // ignore environments that do not allow redefining the property
      element.setAttribute('loading', loading);
    }
  }, [loading]);

  const handleImageError = (event: SyntheticEvent<HTMLImageElement>) => {
    if (!fallback || hasAppliedFallback.current) {
      onError?.(event);
      return;
    }

    hasAppliedFallback.current = true;
    // eslint-disable-next-line no-param-reassign
    event.currentTarget.src = fallback;
    if (event.currentTarget.srcset) {
      // eslint-disable-next-line no-param-reassign
      event.currentTarget.srcset = '';
    }
    onError?.(event);
  };

  const content = (
    <section
      ref={containerRef}
      className={bannerClassName}
      data-testid="banner"
      role={role}
      aria-label={aria}
      onMouseEnter={onBannerMouseEnter}
      onMouseLeave={onBannerMouseLeave}
    >
      <a
        href={banner.cta_url}
        target={banner.cta_target}
        rel={banner.cta_target === '_blank' ? 'noopener noreferrer' : undefined}
        className="catalog-banner__link"
        onClick={onLinkClick}
      >
        <div ref={mediaWrapperRef} className="catalog-banner__media" style={mediaStyle}>
          <picture className="catalog-banner__picture">
            {srcSet ? <source srcSet={srcSet} sizes={sizes} /> : null}
            <img
              src={src}
              srcSet={srcSet}
              sizes={sizes}
              alt={banner.headline ?? ''}
              loading={loading}
              decoding={resolvedDecoding}
              className="catalog-banner__image"
              fetchPriority={fetchPriority}
              onError={handleImageError}
              onMouseEnter={onMediaMouseEnter}
              onMouseLeave={onMediaMouseLeave}
              ref={imageRef}
            />
          </picture>
          <div className="catalog-banner__media-overlay" />
        </div>
        <div ref={textContainerRef} className="catalog-banner__content">
          <header className="catalog-banner__header">
            <h3 ref={headlineRef} className="catalog-banner__headline">
              {banner.headline}
            </h3>
            {banner.body ? (
              <p ref={bodyRef} className="catalog-banner__body">
                {banner.body}
              </p>
            ) : null}
          </header>
          <span ref={ctaRef} className="catalog-banner__cta">
            {banner.cta_label}
          </span>
          {children}
        </div>
      </a>
    </section>
  );

  if (renderAsChild) {
    return content;
  }

  return (
    <Col
      // className="catalogue-panel__col catalogue-panel__col--banner"
      className="catalogue-panel__col--banner"
      style={colStyle}
      xs={24}
    >
      {content}
    </Col>
  );
};

export default CatalogBannerRow;

